import {
  DataEditor,
  GridCellKind,
  type GridSelection,
} from "@glideapps/glide-data-grid";
import "@glideapps/glide-data-grid/dist/index.css";
import { useMemo, useState } from "react";
import { Stack } from "@volca/ui/components/ui/stack";
import { Tabs } from "@volca/ui/components/ui/tabs";
import { cn } from "@volca/ui/lib/utils";
import { makePracticeWorkbook } from "@/help/guides/payroll-edit-workbook";
import {
  adaptSpreadsheetSheet,
  isCellEditable,
} from "@/payroll/ai-script/ai-script-spreadsheet-viewer/adapter";
import { drawOverrideCornerMarker } from "@/payroll/ai-script/ai-script-spreadsheet-viewer/cell-markers";
import { FormulaBar } from "@/payroll/ai-script/ai-script-spreadsheet-viewer/formula-bar";
import { SheetTabList } from "@/payroll/ai-script/ai-script-spreadsheet-viewer/sheet-tab-list";
import {
  buildSpreadsheetTheme,
  volcaColor,
} from "@/payroll/ai-script/ai-script-spreadsheet-viewer/spreadsheet-theme";
import {
  emptySelection,
  formatCoordinate,
  getCellErrorCode,
} from "@/payroll/ai-script/ai-script-spreadsheet-viewer/utils";
import type { IAiSpreadsheetLoadedSheet } from "@/payroll/ai-script/types";
import { ExampleNote } from "./example-ui";

const REVIEW_WORKBOOK = makePracticeWorkbook(200);
interface PracticeGridProps {
  sheet?: IAiSpreadsheetLoadedSheet;
  onCommit?: (rawInput: string, cellId: string) => void;
  isLocked?: boolean;
  editedCellIds?: string[];
  reviewCellIds?: string[];
  showSelectionNote?: boolean;
  compact?: boolean;
}

export const PracticeGrid = ({
  sheet = REVIEW_WORKBOOK,
  onCommit,
  isLocked = false,
  editedCellIds = [],
  reviewCellIds = [],
  showSelectionNote = true,
  compact = false,
}: PracticeGridProps) => {
  const [selection, setSelection] = useState<GridSelection>(emptySelection);
  const [maximized, setMaximized] = useState(false);
  const [switcher, setSwitcher] = useState(false);
  const readOnly = !onCommit || isLocked || !sheet.editable;
  const adapted = useMemo(
    () => adaptSpreadsheetSheet({ ...sheet, editable: !readOnly }),
    [sheet, readOnly],
  );
  const theme = useMemo(buildSpreadsheetTheme, []);
  const previewSize = useMemo(
    () =>
      sheet.cells.reduce(
        (size, cell) => ({
          rows: Math.max(size.rows, cell.row),
          columns: Math.max(size.columns, cell.col),
        }),
        { rows: 0, columns: 0 },
      ),
    [sheet.cells],
  );
  const selected = selection.current?.cell;
  const cell = selected ? adapted.getCellRecord(...selected) : undefined;
  const canEdit = !readOnly && !!cell && isCellEditable(cell);
  const commit = (rawInput: string, cellId: string) => {
    if (!readOnly) onCommit?.(rawInput, cellId);
  };
  return (
    <>
      <Tabs
        value={sheet.id}
        className={cn("flex min-w-0 flex-col bg-surface-raised", {
          "fixed inset-4 z-[900] rounded-lg bg-surface-overlay shadow-overlay":
            maximized,
        })}
      >
        <FormulaBar
          coordinate={selected ? formatCoordinate(selected) : null}
          commitTarget={cell ? { kind: "cell", cellId: cell.id } : null}
          value={cell?.raw_input ?? ""}
          canEdit={canEdit}
          isSheetReadOnly={!onCommit}
          isCellLocked={!!cell && !canEdit}
          errorCode={getCellErrorCode(cell)}
          isRecalculating={false}
          isMaximized={maximized}
          onToggleMaximize={() => setMaximized((value) => !value)}
          onCommit={(raw, target) => {
            if (target.kind === "cell") commit(raw, target.cellId);
          }}
          valueType={cell?.value_type}
        />
        <Stack
          gap={0}
          align="stretch"
          direction="row"
          className={cn("min-h-0 w-full", {
            "h-[180px]": !maximized && compact,
            "h-[260px]": !maximized && !compact && !onCommit,
            "h-[284px]": !maximized && !compact && !!onCommit,
            "flex-1": maximized,
          })}
        >
          <DataEditor
            columns={adapted.columns.slice(0, previewSize.columns)}
            rows={previewSize.rows}
            getCellContent={([col, row]) => adapted.getCellContent(col, row)}
            getCellsForSelection={adapted.getCellsForSelection}
            width="100%"
            height="100%"
            rowHeight={28}
            rowMarkers={{
              kind: "clickable-number",
              width: 36,
              theme: { bgCell: volcaColor("--neutral-subtle") },
            }}
            theme={theme}
            gridSelection={selection}
            onGridSelectionChange={setSelection}
            onCellEdited={([col, row], value) => {
              const record = adapted.getCellRecord(col, row);
              if (
                record &&
                isCellEditable(record) &&
                (value.kind === GridCellKind.Text ||
                  value.kind === GridCellKind.Number)
              )
                commit(String(value.data), record.id);
            }}
            drawCell={(args, draw) => {
              draw();
              const cellId =
                adapted.getCellRecord(args.col, args.row)?.id ?? "";
              const needsReview = reviewCellIds.includes(cellId);
              if (needsReview || editedCellIds.includes(cellId))
                drawOverrideCornerMarker(
                  args.ctx,
                  args.rect,
                  volcaColor(needsReview ? "--amber-9" : "--blue-10"),
                );
            }}
            smoothScrollX
            smoothScrollY
          />
        </Stack>
        <SheetTabList
          sheets={[sheet]}
          activeSheetId={sheet.id}
          isSheetSwitcherOpen={switcher}
          onSheetSwitcherOpenChange={setSwitcher}
          onSheetSelect={() => setSwitcher(false)}
          onSheetPrefetchIntent={() => {}}
        />
      </Tabs>
      {!onCommit && showSelectionNote && (
        <ExampleNote>
          {cell?.kind === "formula"
            ? `${selected ? formatCoordinate(selected) : ""} uses ${cell.raw_input}. Compare the referenced cells with the displayed result.`
            : cell
              ? `Selected ${selected ? formatCoordinate(selected) : ""}: ${cell.computed_value}. Select D2 to inspect the total formula.`
              : "Select a cell to see its value or formula. This review workbook is read-only."}
        </ExampleNote>
      )}
    </>
  );
};
